import { Empty as AntEmpty } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { NotFound } from 'src/components/Errors/NotFound';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { CssVariables } from 'src/styles/CssVariables';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';
import styled from 'styled-components';

export type MetabasePageDetailsProps = {
    store: BasicStore<any>;
};
export const MetabasePageDetails = observer(
    ({ store }: MetabasePageDetailsProps) => {
        const { params } = useRouteMatch<{ entityId: string }>();
        const { entityId } = params;

        const [isIframeLoaded, setIsIframeLoaded] = useState(false);

        const iframeSrc = useMemo(() => {
            return store?.itemLoader?.data?.url;
        }, [store?.itemLoader?.data]);

        useEffect(() => {
            if (!entityId) {
                return;
            }
            store.loadItem(entityId);

            return () => {
                store.formLoader.reset();
                store.itemLoader.reset();
            };
        }, [entityId]);

        const loadingStatus = useMemo(() => {
            return new CombinedLoadingStatus([
                store.itemLoader,
                store.listLoader,
            ]);
        }, []);

        if (loadingStatus.isLoading) {
            return <SpinnerFullWidth />;
        }

        if (
            loadingStatus.isLoaded &&
            (store.listLoader.data?.length || 0) === 0
        ) {
            return <StyledEmpty description={t('Empty.DashboardsList')} />;
        }

        if (store.itemLoader?.errorData?.status === 404) {
            return <NotFound />;
        }

        return (
            <StyledPageWrapper>
                {!isIframeLoaded && (
                    <StyledIframeSpinnerContainer>
                        <SpinnerFullWidth />
                    </StyledIframeSpinnerContainer>
                )}
                <StyledIframe
                    src={iframeSrc}
                    width="100%"
                    onLoad={() => {
                        setIsIframeLoaded(true);
                    }}
                    allowFullScreen
                    allow="geolocation; microphone; camera; midi; encrypted-media; accelerometer; gyroscope; deviceorientation; devicemotion;"
                />
            </StyledPageWrapper>
        );
    },
);

const StyledIframe = styled.iframe`
    height: calc(
        100vh - (${CssVariables.headerHeight} + 4rem)
    ); // Where 4rem is a sum of top and bottom paddings
    border: none;
`;

const StyledIframeSpinnerContainer = styled.div`
    background: #ffffff;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    height: 100%;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const StyledPageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: calc(
        100vh - (${CssVariables.headerHeight} + 4rem)
    ); // Where 4rem is a sum of top and bottom paddings
`;

const StyledEmpty = styled(AntEmpty)`
    .ant-empty-image {
        display: none;
    }
`;
